/**
 * OrderPage — Card-payment order confirmation page.
 * Thin orchestration layer: delegates data fetching to useOrderData hook
 * and rendering to Confirmation component.
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useMediaQuery } from '../../../shared/hooks/useMediaQuery';
import { ORDER_TEXTS, SIDEBAR_BREAKPOINT } from '../constants/order.constants';
import { useOrderData } from '../hooks/useOrderData';

import Confirmation from './Confirmation';
import HotelsNearby from './HotelsNearby';


const OrderPage: React.FC = () => {
    const navigate = useNavigate();
    const { eventKey } = useParams<string>();

    const { order, products, customerEmail, nextSteps, hotelMapInfo, loading, error } = useOrderData();

    /** true when viewport is desktop (sidebar layout). */
    const isDesktop = useMediaQuery(SIDEBAR_BREAKPOINT);

    /**
     * Guard: render HotelsNearby only when the category has BOTH:
     *   1. hotelMapStatus === 'enable'
     *   2. hotelMapUrl is a non-empty string
     * Any other combination (disabled, missing url, missing fields) → hidden.
     */
    const shouldShowHotelMap =
        hotelMapInfo?.status === 'enable' &&
        typeof hotelMapInfo.url === 'string' &&
        hotelMapInfo.url.trim().length > 0;

    const handleReturnHome = () => {
        navigate(`/${eventKey ?? ''}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-gray-600 text-lg">{ORDER_TEXTS.LOADING}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9FAFB]">
            <Confirmation
                orderData={order}
                products={products}
                customerEmail={customerEmail}
                loading={false}
                error={error}
                onReturnHome={handleReturnHome}
                nextSteps={nextSteps}
                /* Sidebar column (Desktop >= 1025px) */
                sidebar={
                    isDesktop ? (
                        <div>
                            {shouldShowHotelMap && (
                                <HotelsNearby bookingUrl={hotelMapInfo!.url} />
                            )}
                        </div>
                    ) : null
                }
                /* Bottom slot (Mobile/Tablet < 1025px) */
                hotelsNearby={
                    shouldShowHotelMap && !isDesktop ? (
                        <HotelsNearby bookingUrl={hotelMapInfo!.url} />
                    ) : null
                }
            />
        </div>
    );
};

export default OrderPage;
